import { useState, useEffect } from "react";
import { usePartiesStore, PartyProfile } from "@/data/partiesStore";
import { getAccreditations, AccreditationRequest } from "@/data/accreditationsStore";
import { addModerationEntry, fetchModerationHistory, fetchSupportTickets, ModerationHistoryEntry } from "@/data/moderationStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CheckCircle, XCircle, FileIcon, Building, Tv, ShieldAlert, Download, Heart, User, Filter, Key, FileText, Ban, CheckCircle2, Search, History } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { getApiUrl } from "@/utils/apiConfig";

const API_BASE_URL = getApiUrl();

type EntityType = 'PARTNER' | 'PARTY';

interface UnifiedDossier {
  id: string;
  type: EntityType;
  data: any; 
  name: string;
  role: string; 
  email?: string;
  phone?: string;
  date: string;
  pendingType: 'ACCREDITATION' | 'METADATA' | 'UNLOCK' | 'NONE';
  status: 'pending' | 'suspens' | 'approved' | 'blocked';
  feedback?: string;
}

export default function AdminEspacesDediesPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("inbox");
  const [messageInputs, setMessageInputs] = useState<Record<string, string>>({});

  // --- PARTIS LOGIC ---
  const { profiles, fetchProfiles, adminValidateProfile, adminAuthorizeProfileModification } = usePartiesStore();
  
  // --- AUTRES PARTENAIRES LOGIC ---
  const [partners, setPartners] = useState<any[]>([]);
  const [accreditationRequests, setAccreditationRequests] = useState<AccreditationRequest[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [repertoireFilter, setRepertoireFilter] = useState("ALL");
  const [repertoireSubFilter, setRepertoireSubFilter] = useState("ALL");

  // --- SUPPORT & HISTORY ---
  const [supportTickets, setSupportTickets] = useState<any[]>([]);
  const [supportFilter, setSupportFilter] = useState("ALL");
  const [supportSubFilter, setSupportSubFilter] = useState("ALL");
  const [showArchivedSupport, setShowArchivedSupport] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [currentHistory, setCurrentHistory] = useState<ModerationHistoryEntry[]>([]);
  const [selectedDossierName, setSelectedDossierName] = useState("");
  
  const loadHistory = async (id: string, name: string) => {
    setSelectedDossierName(name);
    const hist = await fetchModerationHistory(id);
    setCurrentHistory(hist);
    setHistoryModalOpen(true);
  };

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem("kaynin_auth_token");
      const res = await fetch(`${API_BASE_URL}/partners.php?t=${Date.now()}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPartners(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchProfiles();
    fetchPartners();
    fetchSupportTickets().then(setSupportTickets);
    
    setAccreditationRequests(getAccreditations());
    const handleAccredChanged = () => setAccreditationRequests(getAccreditations());
    window.addEventListener("accreditationsChanged", handleAccredChanged);
    return () => window.removeEventListener("accreditationsChanged", handleAccredChanged);
  }, [fetchProfiles]);


  // --- MODERATION ACTIONS ---
  const executeModeration = async (dossierId: string, actionFn: () => Promise<boolean>, successMsg: string, actionType?: 'APPROVE' | 'REJECT' | 'REQUIRE_PROOF', type?: 'PARTNER' | 'PARTY') => {
    setIsSaving(true);
    const success = await actionFn();
    setIsSaving(false);
    if (success) {
      toast({ title: "Action réussie", description: successMsg });
      
      if (actionType && type) {
        const msg = messageInputs[dossierId] || "";
        await addModerationEntry(dossierId, type, actionType, msg);
      }

      setMessageInputs(prev => ({...prev, [dossierId]: ""}));
      fetchPartners();
      fetchProfiles();
    }
  };

  const handleApprove = async (d: UnifiedDossier) => {
    const msg = messageInputs[d.id] || "";
    
    executeModeration(d.id, async () => {
      if (d.type === 'PARTY') {
        if (d.pendingType === 'UNLOCK') {
          await usePartiesStore.getState().moderateProfileUnlock(d.id, "approved", msg);
          return true;
        } else {
          await adminValidateProfile(d.id, "valide", user?.name || "Admin", msg);
          return true;
        }
      } else {
        const token = localStorage.getItem("kaynin_auth_token");
        const actionStr = d.pendingType === 'UNLOCK' ? 'moderate_unlock' : d.pendingType === 'METADATA' ? 'admin_moderate_metadata' : 'admin_moderate_partner_accreditation';
        const res = await fetch(`${API_BASE_URL}/auth.php`, {
          method: "POST",
          headers: { "Content-Type": "text/plain", "Authorization": `Bearer ${token}` },
          body: btoa(unescape(encodeURIComponent(JSON.stringify({ action: actionStr, userId: d.id, status: d.pendingType === 'METADATA' ? 'valide' : 'approved', feedback: msg }))))
        });
        return res.ok;
      }
    }, "Le dossier a été validé avec succès.", "APPROVE", d.type);
  };

  const handleReject = async (d: UnifiedDossier) => {
    const msg = messageInputs[d.id] || "";
    if (!msg) return toast({ title: "Erreur", description: "Veuillez fournir un motif de rejet.", variant: "destructive" });

    executeModeration(d.id, async () => {
      if (d.type === 'PARTY') {
        if (d.pendingType === 'UNLOCK') {
          await usePartiesStore.getState().moderateProfileUnlock(d.id, "rejected", msg);
          return true;
        } else {
          await adminValidateProfile(d.id, "rejete", user?.name || "Admin", msg);
          return true;
        }
      } else {
        const token = localStorage.getItem("kaynin_auth_token");
        const actionStr = d.pendingType === 'UNLOCK' ? 'moderate_unlock' : d.pendingType === 'METADATA' ? 'admin_moderate_metadata' : 'admin_moderate_partner_accreditation';
        const res = await fetch(`${API_BASE_URL}/auth.php`, {
          method: "POST",
          headers: { "Content-Type": "text/plain", "Authorization": `Bearer ${token}` },
          body: btoa(unescape(encodeURIComponent(JSON.stringify({ action: actionStr, userId: d.id, status: d.pendingType === 'METADATA' ? 'rejete' : 'rejected', feedback: msg }))))
        });
        return res.ok;
      }
    }, "Le dossier a été rejeté.", "REJECT", d.type);
  };

  const handleRequireProof = async (d: UnifiedDossier) => {
    const msg = messageInputs[d.id] || "";
    if (!msg) return toast({ title: "Erreur", description: "Veuillez préciser la preuve demandée.", variant: "destructive" });

    if (d.type === 'PARTNER') {
      executeModeration(d.id, async () => {
        const token = localStorage.getItem("kaynin_auth_token");
        const res = await fetch(`${API_BASE_URL}/auth.php`, {
          method: "POST",
          headers: { "Content-Type": "text/plain", "Authorization": `Bearer ${token}` },
          body: btoa(unescape(encodeURIComponent(JSON.stringify({ action: 'admin_moderate_partner_accreditation', userId: d.id, status: 'requires_proof', feedback: msg }))))
        });
        return res.ok;
      }, "Une demande de preuve a été envoyée.", "REQUIRE_PROOF", d.type);
    }
  };

  const handleBlockToggle = async (d: UnifiedDossier) => {
    const isBlocked = d.status === 'blocked';
    const newStatus = isBlocked ? 'approved' : 'blocked';
    
    if (d.type === 'PARTNER') {
      executeModeration(d.id, async () => {
        const token = localStorage.getItem("kaynin_auth_token");
        const res = await fetch(`${API_BASE_URL}/auth.php`, {
          method: "POST",
          headers: { "Content-Type": "text/plain", "Authorization": `Bearer ${token}` },
          body: btoa(unescape(encodeURIComponent(JSON.stringify({ action: 'admin_moderate_partner_accreditation', userId: d.id, status: newStatus, feedback: isBlocked ? 'Débloqué' : 'Bloqué par admin' }))))
        });
        return res.ok;
      }, isBlocked ? "L'accès a été rétabli." : "Le partenaire a été bloqué.");
    }
  };


  // --- DATA MAPPING ---
  const allDossiers: UnifiedDossier[] = [
    ...profiles.map(p => {
      let pendingType: UnifiedDossier['pendingType'] = 'NONE';
      if (p.unlockRequestStatus === 'pending') pendingType = 'UNLOCK';
      else if (p.status === 'en_attente') pendingType = 'METADATA';

      let status: UnifiedDossier['status'] = 'approved';
      if (p.status === 'en_attente' || p.unlockRequestStatus === 'pending') status = 'pending';
      else if (p.status === 'rejete') status = 'suspens';

      return {
        id: p.acronym, type: 'PARTY', data: p, name: p.fullNameFr || p.acronym, role: 'PARTY',
        email: p.contactEmail, phone: p.contactPhone, date: p.submittedAt || new Date().toISOString(),
        pendingType, status, feedback: p.lastJustification
      } as UnifiedDossier;
    }),
    ...partners.map(u => {
      let pendingType: UnifiedDossier['pendingType'] = 'NONE';
      if (u.unlockRequestStatus === 'pending') pendingType = 'UNLOCK';
      else if (u.metadataStatus === 'en_attente') pendingType = 'METADATA';
      else if (u.accreditationStatus === 'pending' || u.accreditationStatus === 'none') pendingType = 'ACCREDITATION';

      let status: UnifiedDossier['status'] = 'approved';
      if (u.accreditationStatus === 'blocked') status = 'blocked';
      else if (u.accreditationStatus === 'requires_proof' || u.accreditationStatus === 'rejected') status = 'suspens';
      else if (pendingType !== 'NONE') status = 'pending';

      return {
        id: u.id, type: 'PARTNER', data: u, name: u.name, role: u.role,
        email: u.email, phone: u.phone, date: u.registeredAt || new Date().toISOString(),
        pendingType, status, feedback: u.accreditationFeedback
      } as UnifiedDossier;
    })
  ];

  const inboxDossiers = allDossiers.filter(d => d.status === 'pending').sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const suspensDossiers = allDossiers.filter(d => d.status === 'suspens');
  const baseRepertoireDossiers = allDossiers.filter(d => d.status === 'approved' || d.status === 'blocked');
  const repertoireDossiers = baseRepertoireDossiers.filter(d => {
    // 1. Role Filter
    if (repertoireFilter === 'PARTY_PROFILE' && d.type !== 'PARTY') return false;
    if (repertoireFilter === 'DELEGATE' && (d.type !== 'PARTNER' || d.role !== 'PARTY')) return false;
    if (['ONG', 'MEDIA', 'INSTITUTION'].includes(repertoireFilter) && (d.role !== repertoireFilter || d.type !== 'PARTNER')) return false;

    // 2. Sub-Filter (Specific Entity)
    if (repertoireSubFilter !== 'ALL') {
      if (repertoireFilter === 'PARTY_PROFILE' && d.id !== repertoireSubFilter) return false;
      if (repertoireFilter === 'DELEGATE' && d.data.partyAcronym !== repertoireSubFilter) return false;
      if (repertoireFilter === 'ONG' && d.data.ongName !== repertoireSubFilter) return false;
      if (repertoireFilter === 'MEDIA' && d.data.channelName !== repertoireSubFilter) return false;
      if (repertoireFilter === 'INSTITUTION' && d.data.institutionName !== repertoireSubFilter) return false;
    }
    return true;
  });

  const getSubFilterOptions = () => {
    if (repertoireFilter === 'ALL') return [];
    
    const dossiers = baseRepertoireDossiers.filter(d => {
      if (repertoireFilter === 'PARTY_PROFILE') return d.type === 'PARTY';
      if (repertoireFilter === 'DELEGATE') return d.type === 'PARTNER' && d.role === 'PARTY';
      return d.role === repertoireFilter && d.type === 'PARTNER';
    });

    const options = new Set<string>();
    dossiers.forEach(d => {
      if (repertoireFilter === 'PARTY_PROFILE') options.add(d.id);
      else if (repertoireFilter === 'DELEGATE' && d.data.partyAcronym) options.add(d.data.partyAcronym);
      else if (repertoireFilter === 'ONG' && d.data.ongName) options.add(d.data.ongName);
      else if (repertoireFilter === 'MEDIA' && d.data.channelName) options.add(d.data.channelName);
      else if (repertoireFilter === 'INSTITUTION' && d.data.institutionName) options.add(d.data.institutionName);
    });
    
    return Array.from(options).sort();
  };
  const subFilterOptions = getSubFilterOptions();

  // --- SUPPORT FILTERING ---
  const filteredSupportTickets = supportTickets.filter(t => {
    // Check archive status
    if (showArchivedSupport && t.status !== 'closed') return false;
    if (!showArchivedSupport && t.status === 'closed') return false;
    
    // Category & Sub-filters
    if (supportFilter !== 'ALL' && t.user_role !== supportFilter) return false;
    if (supportSubFilter !== 'ALL' && t.entity_name !== supportSubFilter) return false;
    return true;
  });

  const getSupportSubFilterOptions = () => {
    if (supportFilter === 'ALL') return [];
    const options = new Set<string>();
    supportTickets.filter(t => t.user_role === supportFilter).forEach(t => {
      if (t.entity_name) options.add(t.entity_name);
    });
    return Array.from(options).sort();
  };
  const supportSubFilterOptions = getSupportSubFilterOptions();


  // --- UI COMPONENTS ---
  const RoleIcon = ({ role }: { role: string }) => {
    if (role === 'MEDIA') return <Tv className="w-5 h-5" />;
    if (role === 'ONG') return <Heart className="w-5 h-5" />;
    if (role === 'PARTY') return <Building className="w-5 h-5" />;
    return <User className="w-5 h-5" />;
  };

  const getRoleColor = (role: string) => {
    if (role === 'MEDIA') return 'bg-purple-100 text-purple-700';
    if (role === 'ONG') return 'bg-accent/10 text-accent';
    if (role === 'PARTY') return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getPendingTypeBadge = (d: UnifiedDossier) => {
    if (d.pendingType === 'UNLOCK') return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 border-none"><Key className="w-3 h-3 mr-1"/> Demande de Déverrouillage</Badge>;
    if (d.pendingType === 'METADATA') return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-none"><ShieldAlert className="w-3 h-3 mr-1"/> Modification Profil</Badge>;
    if (d.pendingType === 'ACCREDITATION') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-none"><User className="w-3 h-3 mr-1"/> Nouvelle Inscription</Badge>;
    return null;
  };

  const renderDossierCard = (d: UnifiedDossier, isRepertoire = false) => {
    const req = d.type === 'PARTNER' ? accreditationRequests.find(r => r.userId === d.id || r.email === d.email) : null;
    
    return (
      <Card key={`${d.type}-${d.id}`} className="overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow">
        <div className={`h-1.5 w-full ${d.role === 'ONG' ? 'bg-accent' : d.role === 'MEDIA' ? 'bg-purple-500' : 'bg-blue-500'}`} />
        <div className="p-4">
          {/* HEADER */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRoleColor(d.role)}`}>
                <RoleIcon role={d.role} />
              </div>
              <div>
                <h4 className="font-bold text-base flex flex-wrap items-center gap-2">
                  {d.name}
                  {d.status === 'blocked' && <Badge variant="destructive">Accès Bloqué</Badge>}
                </h4>
                <p className="text-xs text-muted-foreground">{d.email}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {d.type === 'PARTY' ? (
                <Badge className="font-mono bg-blue-100 text-blue-800 border-none">PROFIL PARTI</Badge>
              ) : d.type === 'PARTNER' && d.role === 'PARTY' ? (
                <Badge className="font-mono bg-blue-600 text-white border-none">DÉLÉGUÉ</Badge>
              ) : (
                <Badge variant="outline" className="font-mono">{d.role}</Badge>
              )}
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground hover:text-emerald-600" onClick={() => loadHistory(d.id, d.name)}>
                <History className="w-3 h-3 mr-1" /> Historique
              </Button>
            </div>
          </div>

          {/* DETAILS */}
          <div className="bg-muted/30 p-3 rounded-lg text-xs space-y-2 mb-4 border border-border/50">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Téléphone :</span> <span className="font-semibold">{d.phone || '-'}</span></div>
              <div><span className="text-muted-foreground">Date :</span> <span className="font-semibold">{d.date ? new Date(d.date).toLocaleDateString('fr-FR') : '-'}</span></div>
            </div>
            
            {d.type === 'PARTY' && d.pendingType === 'METADATA' && (
               <div className="pt-2 border-t border-border/50">
                 <span className="text-muted-foreground font-semibold block mb-1">Motif / Modifications soumises :</span>
                 <p className="font-bold text-foreground bg-white p-2 rounded border text-sm">{d.data.focusFr || 'Profil mis à jour'}</p>
                 {d.data.programmePdfUrl && <span className="inline-flex items-center gap-1 text-blue-600 mt-1"><FileText className="w-3 h-3"/> PDF Inclus</span>}
               </div>
            )}

            {d.type === 'PARTNER' && d.pendingType === 'METADATA' && d.data.pendingMetadata && (
               <div className="pt-2 border-t border-border/50">
                 <span className="text-muted-foreground font-semibold block mb-1">Champs modifiés :</span>
                 <p className="font-bold text-foreground bg-white p-2 rounded border text-sm">{Object.keys(d.data.pendingMetadata).join(', ')}</p>
               </div>
            )}

            {d.type === 'PARTNER' && (
              <div className="pt-2 border-t border-border/50 grid grid-cols-1 md:grid-cols-2 gap-2">
                 {d.role === 'ONG' && <><div className="md:col-span-2"><span className="text-muted-foreground">ONG :</span> <span className="font-semibold">{d.data.ongName || '-'}</span></div><div className="md:col-span-2"><span className="text-muted-foreground">Activité :</span> <span className="font-semibold">{d.data.champsActivite || '-'}</span></div></>}
                 {d.role === 'MEDIA' && <><div><span className="text-muted-foreground">Média :</span> <span className="font-semibold">{d.data.channelName || '-'}</span></div><div><span className="text-muted-foreground">Type :</span> <span className="font-semibold">{d.data.mediaType || '-'}</span></div></>}
                 {d.role === 'INSTITUTION' && <div className="md:col-span-2"><span className="text-muted-foreground">Institution :</span> <span className="font-semibold">{d.data.institutionName || '-'}</span></div>}
                 {d.role === 'PARTY' && <>
                    <div className="md:col-span-2"><span className="text-muted-foreground">Parti représenté :</span> <span className="font-semibold">{d.data.partyAcronym || '-'}</span></div>
                    <div><span className="text-muted-foreground">Qualité :</span> <span className="font-semibold">{d.data.qualite || '-'}</span></div>
                    <div><span className="text-muted-foreground">Genre :</span> <span className="font-semibold">{d.data.genre || '-'}</span></div>
                    <div><span className="text-muted-foreground">Âge :</span> <span className="font-semibold">{d.data.age || '-'}</span></div>
                    <div><span className="text-muted-foreground">Résidence :</span> <span className="font-semibold">{d.data.residence || '-'}</span></div>
                 </>}
              </div>
            )}

            {d.pendingType === 'UNLOCK' && (
              <div className="pt-2 border-t border-border/50 text-purple-800 bg-purple-50/50 p-2 rounded mt-2">
                <span className="font-bold block mb-1">Motif du déverrouillage :</span>
                <p className="font-semibold text-sm">"{d.data.unlockRequestMessage}"</p>
              </div>
            )}

            {/* Document Justificatif for accreditations AND repertoire */}
            {(req?.documentUrl || d.data.documentUrl || d.data.programmePdfUrl || d.data.metadata?.documentUrl || d.data.pendingMetadata?.documentUrl) && (
              <a 
                href={req?.documentUrl || d.data.documentUrl || d.data.programmePdfUrl || d.data.metadata?.documentUrl || d.data.pendingMetadata?.documentUrl} 
                target="_blank" 
                rel="noreferrer" 
                download
                className="inline-flex items-center justify-center rounded-md text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-blue-200 bg-blue-50 text-blue-700 shadow-sm hover:bg-blue-100 h-9 px-4 py-2 w-full mt-3 gap-2"
              >
                <Download className="w-4 h-4" /> Voir la pièce jointe / Justificatif
              </a>
            )}

            {/* Feedback / Suspens Info */}
            {d.status === 'suspens' && d.feedback && (
              <div className="pt-2 border-t border-red-200 text-red-700 bg-red-50 p-2 rounded mt-2">
                 <span className="font-bold block mb-1">Motif de rejet / demande de preuve :</span>
                 <p>{d.feedback}</p>
              </div>
            )}
          </div>

          {/* ACTIONS */}
          {!isRepertoire ? (
            <div className="space-y-3">
              <Input 
                placeholder={d.status === 'suspens' ? "Envoyer un nouveau message..." : "Motif (obligatoire si refus/preuve)..."} 
                value={messageInputs[d.id] || ""} 
                onChange={(e) => setMessageInputs(prev => ({...prev, [d.id]: e.target.value}))}
                className="h-9 text-xs"
              />
              <div className="flex gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-1 h-9" disabled={isSaving} onClick={() => handleApprove(d)}>
                  <CheckCircle className="w-4 h-4 mr-2" /> {d.status === 'suspens' ? 'Réévaluer (Valider)' : 'Valider'}
                </Button>
                {d.type === 'PARTNER' && d.pendingType === 'ACCREDITATION' && (
                  <Button size="sm" variant="outline" className="border-sky-200 text-sky-700 hover:bg-sky-50 flex-1 h-9" disabled={isSaving} onClick={() => handleRequireProof(d)}>
                    Preuve
                  </Button>
                )}
                <Button size="sm" className="bg-red-600 hover:bg-red-700 flex-1 h-9" disabled={isSaving} onClick={() => handleReject(d)}>
                  <XCircle className="w-4 h-4 mr-2" /> Rejeter
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className={d.status === 'blocked' ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50 w-full" : "border-red-200 text-red-700 hover:bg-red-50 w-full"} disabled={isSaving} onClick={() => handleBlockToggle(d)}>
                {d.status === 'blocked' ? <><CheckCircle className="w-4 h-4 mr-2" /> Débloquer</> : <><Ban className="w-4 h-4 mr-2" /> Bloquer l'accès</>}
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  };


  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h1 className="text-xl md:text-3xl font-display font-bold text-foreground">Gestion des Espaces Dédiés</h1>
        <p className="text-muted-foreground font-body text-sm mt-1">
          Centre de contrôle unifié : Inscriptions, publications et modifications (Partis, ONGs, Médias, Institutions).
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 h-auto bg-muted/50 p-1 flex flex-wrap md:flex-nowrap gap-1 rounded-xl">
          <TabsTrigger value="inbox" className="rounded-lg flex-1 font-semibold flex items-center justify-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
            <User className="w-4 h-4" /> Boîte de réception 
            {inboxDossiers.length > 0 && <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs ml-1">{inboxDossiers.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="suspens" className="rounded-lg flex-1 font-semibold flex items-center justify-center gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-white">
            <Filter className="w-4 h-4" /> Dossiers en suspens
            {suspensDossiers.length > 0 && <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs ml-1">{suspensDossiers.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="repertoire" className="rounded-lg flex-1 font-semibold flex items-center justify-center gap-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Building className="w-4 h-4" /> Répertoire des Partenaires
          </TabsTrigger>
          <TabsTrigger value="support" className="rounded-lg flex-1 font-semibold flex items-center justify-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <User className="w-4 h-4" /> Demandes Support
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-0">
          {inboxDossiers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-dashed border-border/60 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300 mb-3" />
              <h3 className="text-lg font-bold text-foreground">Tout est à jour !</h3>
              <p className="text-sm mt-1">Il n'y a aucune nouvelle demande à traiter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {inboxDossiers.map(d => renderDossierCard(d))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="suspens" className="mt-0">
          {suspensDossiers.length === 0 ? (
             <div className="text-center py-12 bg-white rounded-xl border border-dashed border-border/60 text-muted-foreground">
               <Filter className="w-12 h-12 mx-auto text-amber-300 mb-3 opacity-50" />
               <p className="text-sm">Aucun dossier en attente de preuve ou rejeté.</p>
             </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {suspensDossiers.map(d => renderDossierCard(d))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="repertoire" className="mt-0 space-y-4">
          <div className="bg-white p-4 rounded-xl border border-border shadow-sm mb-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-4">
               <div className="flex items-center gap-4">
                 <Search className="w-5 h-5 text-muted-foreground shrink-0" />
                 <p className="text-sm font-medium text-muted-foreground">Filtrez et organisez facilement le répertoire des partenaires.</p>
               </div>
               
               <div className="flex flex-wrap gap-2">
                  <Button variant={repertoireFilter === 'ALL' ? 'default' : 'outline'} size="sm" onClick={() => { setRepertoireFilter('ALL'); setRepertoireSubFilter('ALL'); }} className="h-8 text-xs font-bold">Tous</Button>
                  <Button variant={repertoireFilter === 'PARTY_PROFILE' ? 'default' : 'outline'} size="sm" onClick={() => { setRepertoireFilter('PARTY_PROFILE'); setRepertoireSubFilter('ALL'); }} className="h-8 text-xs font-bold bg-blue-100 text-blue-800 hover:bg-blue-200 border-none">Profils Partis</Button>
                  <Button variant={repertoireFilter === 'DELEGATE' ? 'default' : 'outline'} size="sm" onClick={() => { setRepertoireFilter('DELEGATE'); setRepertoireSubFilter('ALL'); }} className="h-8 text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 border-none">Délégués</Button>
                  <Button variant={repertoireFilter === 'ONG' ? 'default' : 'outline'} size="sm" onClick={() => { setRepertoireFilter('ONG'); setRepertoireSubFilter('ALL'); }} className="h-8 text-xs font-bold">ONGs</Button>
                  <Button variant={repertoireFilter === 'MEDIA' ? 'default' : 'outline'} size="sm" onClick={() => { setRepertoireFilter('MEDIA'); setRepertoireSubFilter('ALL'); }} className="h-8 text-xs font-bold">Médias</Button>
                  <Button variant={repertoireFilter === 'INSTITUTION' ? 'default' : 'outline'} size="sm" onClick={() => { setRepertoireFilter('INSTITUTION'); setRepertoireSubFilter('ALL'); }} className="h-8 text-xs font-bold">Institutions</Button>
               </div>
            </div>

            {repertoireFilter !== 'ALL' && subFilterOptions.length > 0 && (
              <div className="w-full pt-4 border-t border-border flex items-center gap-3">
                <span className="text-sm font-semibold text-muted-foreground">Sélection par organisation :</span>
                <select 
                  value={repertoireSubFilter} 
                  onChange={(e) => setRepertoireSubFilter(e.target.value)}
                  className="h-9 w-full md:w-64 text-sm rounded-md border border-input bg-background px-3 py-1 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="ALL">Afficher tout...</option>
                  {subFilterOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {repertoireDossiers.map(d => renderDossierCard(d, true))}
          </div>
        </TabsContent>

        <TabsContent value="support" className="mt-0 space-y-4">
          <div className="bg-white p-4 rounded-xl border border-border shadow-sm mb-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-4">
               <div className="flex items-center gap-4">
                 <Search className="w-5 h-5 text-muted-foreground shrink-0" />
                 <p className="text-sm font-medium text-muted-foreground">Filtrez les demandes de support par catégorie.</p>
               </div>
               
               <div className="flex flex-wrap items-center gap-2">
                  <Button variant={showArchivedSupport ? "secondary" : "outline"} size="sm" onClick={() => setShowArchivedSupport(!showArchivedSupport)} className="h-8 text-xs font-bold mr-2 border-dashed">
                    {showArchivedSupport ? "← Retour aux requêtes actives" : "Voir les archives (libérer espace)"}
                  </Button>
                  <Button variant={supportFilter === 'ALL' ? 'default' : 'outline'} size="sm" onClick={() => { setSupportFilter('ALL'); setSupportSubFilter('ALL'); }} className="h-8 text-xs font-bold">Toutes</Button>
                  <Button variant={supportFilter === 'PARTY' ? 'default' : 'outline'} size="sm" onClick={() => { setSupportFilter('PARTY'); setSupportSubFilter('ALL'); }} className="h-8 text-xs font-bold">Partis</Button>
                  <Button variant={supportFilter === 'ONG' ? 'default' : 'outline'} size="sm" onClick={() => { setSupportFilter('ONG'); setSupportSubFilter('ALL'); }} className="h-8 text-xs font-bold">ONGs</Button>
                  <Button variant={supportFilter === 'MEDIA' ? 'default' : 'outline'} size="sm" onClick={() => { setSupportFilter('MEDIA'); setSupportSubFilter('ALL'); }} className="h-8 text-xs font-bold">Médias</Button>
                  <Button variant={supportFilter === 'INSTITUTION' ? 'default' : 'outline'} size="sm" onClick={() => { setSupportFilter('INSTITUTION'); setSupportSubFilter('ALL'); }} className="h-8 text-xs font-bold">Institutions</Button>
               </div>
            </div>

            {supportFilter !== 'ALL' && supportSubFilterOptions.length > 0 && (
              <div className="w-full pt-4 border-t border-border flex items-center gap-3">
                <span className="text-sm font-semibold text-muted-foreground">Sélection par organisation :</span>
                <select 
                  value={supportSubFilter} 
                  onChange={(e) => setSupportSubFilter(e.target.value)}
                  className="h-9 w-full md:w-64 text-sm rounded-md border border-input bg-background px-3 py-1 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="ALL">Afficher tout...</option>
                  {supportSubFilterOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {filteredSupportTickets.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-dashed border-border/60 text-muted-foreground">
               <User className="w-12 h-12 mx-auto text-blue-300 mb-3 opacity-50" />
               <h3 className="text-lg font-bold text-foreground">Aucune demande de support</h3>
               <p className="text-sm mt-1">Vous n'avez aucune demande d'assistance pour cette sélection.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredSupportTickets.map(ticket => (
                <Card key={`ticket-${ticket.id}`} className="overflow-hidden border border-border shadow-sm">
                  <div className={`h-1.5 w-full ${ticket.status === 'pending' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="font-mono w-fit">{ticket.user_role}</Badge>
                          {ticket.entity_name ? (
                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 border-none font-bold uppercase">{ticket.entity_name}</Badge>
                          ) : (
                            <Badge variant="secondary" className="font-mono w-fit opacity-50">Non spécifié</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground font-medium">
                          Délégué / Demandeur : <strong className="text-foreground">{ticket.user_name || 'Non spécifié'}</strong>
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-xs font-bold text-foreground bg-muted px-2 py-0.5 rounded-full whitespace-nowrap">{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</span>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{new Date(ticket.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <h4 className="font-bold text-base mb-1 mt-2">{ticket.subject}</h4>
                    <p className="text-sm mb-3 bg-muted/30 p-2 rounded border border-border/50">{ticket.message}</p>
                    
                    {ticket.document_url && (
                      <a href={ticket.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline mb-3">
                        <FileText className="w-3 h-3" /> Pièce jointe
                      </a>
                    )}

                    {ticket.status === 'pending' ? (
                      <div className="space-y-2 mt-2 pt-2 border-t border-border/50">
                         <Input 
                           placeholder="Réponse de l'administration..." 
                           value={messageInputs[`ticket-${ticket.id}`] || ""} 
                           onChange={(e) => setMessageInputs(prev => ({...prev, [`ticket-${ticket.id}`]: e.target.value}))}
                           className="h-9 text-xs"
                         />
                         <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 h-9" onClick={async () => {
                           const msg = messageInputs[`ticket-${ticket.id}`];
                           if (!msg) return toast({title: "Erreur", description: "Veuillez taper une réponse.", variant: "destructive"});
                           setIsSaving(true);
                           const { resolveSupportTicket } = await import('@/data/moderationStore');
                           await resolveSupportTicket(ticket.id, msg);
                           setIsSaving(false);
                           toast({title: "Succès", description: "Le ticket a été résolu."});
                           const { fetchSupportTickets } = await import('@/data/moderationStore');
                           fetchSupportTickets().then(setSupportTickets);
                         }} disabled={isSaving}>
                           Marquer comme résolu
                         </Button>
                      </div>
                    ) : (
                      <div className="mt-2 pt-2 border-t border-emerald-200 text-emerald-800 bg-emerald-50 p-2 rounded text-sm relative">
                        <span className="font-bold block text-xs mb-1">Réponse admin :</span>
                        {ticket.admin_response}
                        
                        <div className="flex gap-2 mt-3 pt-2 border-t border-emerald-200/50">
                          {ticket.status !== 'closed' && (
                            <Button size="sm" variant="outline" className="flex-1 text-xs h-7 border-emerald-300 hover:bg-emerald-100" onClick={async () => {
                              if (!confirm("Voulez-vous archiver ce ticket pour libérer l'espace ?")) return;
                              const { archiveSupportTicket, fetchSupportTickets } = await import('@/data/moderationStore');
                              await archiveSupportTicket(ticket.id);
                              fetchSupportTickets().then(setSupportTickets);
                              toast({description: "Ticket archivé avec succès."});
                            }}>
                              Archiver
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="flex-1 text-xs h-7 border-red-200 text-red-600 hover:bg-red-50" onClick={async () => {
                            if (!confirm("Supprimer définitivement ce ticket ? Cette action est irréversible.")) return;
                            const { deleteSupportTicket, fetchSupportTickets } = await import('@/data/moderationStore');
                            await deleteSupportTicket(ticket.id);
                            fetchSupportTickets().then(setSupportTickets);
                            toast({description: "Ticket supprimé définitivement."});
                          }}>
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

      </Tabs>

      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historique des actions</DialogTitle>
            <DialogDescription>
              Dossier : <strong className="text-foreground">{selectedDossierName}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {currentHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Aucun historique enregistré pour ce dossier.</p>
            ) : (
              <div className="space-y-3">
                {currentHistory.map(h => (
                  <div key={h.id} className={`p-4 rounded-xl border ${h.action_type === 'PARTNER_REACTION' ? 'bg-blue-50/50 border-blue-100' : 'bg-red-50/30 border-red-100'} shadow-sm flex flex-col gap-2`}>
                    <div className="flex justify-between items-center text-xs">
                      <span className={`font-bold ${h.action_type === 'PARTNER_REACTION' ? 'text-blue-700' : 'text-red-700'}`}>
                        {h.action_type === 'REJECT' ? 'Rejet' : h.action_type === 'REQUIRE_PROOF' ? 'Demande de preuve' : h.action_type === 'APPROVE' ? 'Validation' : 'Réponse Partenaire'}
                      </span>
                      <span className="text-muted-foreground">{new Date(h.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                    <p className="text-sm text-foreground">{h.message}</p>
                    {h.document_url && (
                      <a href={h.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1 mt-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                        <FileText className="w-3 h-3" /> Voir la pièce jointe
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
